import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { renderShareCard, shareVariantsFor, type ShareData, type ShareVariant } from '../lib/shareImage';
import { shareImageBlob } from '../lib/share';
import { tapLight, tapMedium } from '../lib/haptics';

interface RenderedCard {
  variant: ShareVariant;
  dataUrl: string;
  blob: Blob;
}

const VARIANT_LABEL: Record<ShareVariant, string> = {
  'completion-light': 'Day complete · Light',
  'completion-dark': 'Day complete · Dark',
  'streak-light': 'Streak · Light',
  'streak-dark': 'Streak · Dark',
};

export function ShareAccomplishmentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state as ShareData | null;

  const [cards, setCards] = useState<RenderedCard[]>([]);
  const [selected, setSelected] = useState(0);
  const [sharing, setSharing] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const valid = !!data && typeof data.earned === 'number';

  useEffect(() => {
    if (!valid) return;
    let cancelled = false;
    (async () => {
      const variants = shareVariantsFor(data);
      const rendered: RenderedCard[] = [];
      for (const v of variants) {
        const card = await renderShareCard(v, data);
        if (cancelled) return;
        rendered.push(card);
        // Reveal cards as they finish so the first is interactive quickly.
        setCards([...rendered]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [valid]);

  if (!valid) return <Navigate to="/" replace />;

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || cards.length === 0) return;
    // Per-card pitch includes the inter-card gap, so the index stays accurate
    // for later cards rather than drifting by one gap-width per slide.
    const pitch = el.scrollWidth / cards.length;
    const idx = Math.min(cards.length - 1, Math.max(0, Math.round(el.scrollLeft / pitch)));
    if (idx !== selected) {
      setSelected(idx);
      tapLight();
    }
  };

  const handleShare = async () => {
    const card = cards[selected];
    if (!card || sharing) return;
    setSharing(true);
    tapMedium();
    try {
      await shareImageBlob(card.blob, `chortle-${card.variant}.png`, 'I cleared my chores today with Chortle! 🎉');
    } catch {
      // Share failed unexpectedly — leave the user on the page to retry.
    } finally {
      setSharing(false);
    }
  };

  const loading = cards.length === 0;

  return (
    <div className="page share-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          className="topbar-icon-btn"
          aria-label="Back"
          onClick={() => { tapLight(); navigate(-1); }}
        >
          <ion-icon name="chevron-back" />
        </button>
        <h1 style={{ margin: 0 }}>Share your day</h1>
      </div>

      <p className="share-subtitle">Swipe to pick a card, then share your win.</p>

      <div className="share-carousel" ref={trackRef} onScroll={onScroll}>
        {loading
          ? [0, 1].map((i) => <div key={i} className="share-slide"><div className="share-image-frame share-skeleton" /></div>)
          : cards.map((card) => (
              <div key={card.variant} className="share-slide">
                <div className="share-image-frame">
                  <img src={card.dataUrl} alt={VARIANT_LABEL[card.variant]} className="share-image" />
                </div>
              </div>
            ))}
      </div>

      {!loading && cards.length > 1 && (
        <div className="share-dots" role="tablist" aria-label="Card variations">
          {cards.map((card, i) => (
            <span key={card.variant} className={`share-dot${i === selected ? ' share-dot-active' : ''}`} />
          ))}
        </div>
      )}

      <div className="share-actions">
        <button
          className="btn btn-primary btn-full"
          onClick={() => void handleShare()}
          disabled={loading || sharing}
        >
          <ion-icon name="share-social" style={{ fontSize: '1.2rem' }} />
          {sharing ? 'Opening…' : 'Share'}
        </button>
      </div>
    </div>
  );
}
